import { BadgeCheck, ChevronLeft, ChevronRight, Gem, Globe, MessageCircle, Send, Trophy, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function mediaUrl(url) {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
}

function buildSocialLinks(channels = {}) {
  return [
    channels?.whatsapp && {
      label: 'WhatsApp',
      value: channels.whatsapp,
      href: `https://wa.me/${String(channels.whatsapp).replace(/[^\d]/g, '')}`,
      icon: MessageCircle,
      tone: 'from-emerald-400 to-green-600',
      action: 'Chat',
    },
    channels?.telegram && {
      label: 'Telegram',
      value: channels.telegram,
      href: String(channels.telegram).startsWith('http')
        ? channels.telegram
        : `https://t.me/${String(channels.telegram).replace(/^@/, '')}`,
      icon: Send,
      tone: 'from-sky-400 to-cyan-600',
      action: 'Join',
    },
    channels?.messenger && {
      label: 'Messenger',
      value: 'Facebook Messenger',
      href: channels.messenger,
      icon: MessageCircle,
      tone: 'from-blue-500 to-indigo-600',
      action: 'Open',
    },
  ].filter(Boolean);
}

export default function BrandTab({ goBack, tenantName = 'Vegas Lobby', logoUrl, supportChannels = {}, games = [] }) {
  const resolvedLogo = mediaUrl(logoUrl) || '/vegas-lobby-logo.png';
  const socialLinks = buildSocialLinks(supportChannels);
  const gameCount = Math.max((games || []).length, 18);

  return (
    <div
      className="vl-pirate-page vl-brand-page min-h-full px-4 py-3 pb-24 text-white"
      style={{
        background:
          'radial-gradient(circle at 18% 0%, rgba(212,175,55,0.13), transparent 30%), radial-gradient(circle at 82% 8%, rgba(20,149,108,0.16), transparent 32%), linear-gradient(180deg, #073027 0%, #061718 58%, #030b0d 100%)',
      }}
      data-testid="brand-page"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={goBack}
            className="w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-105"
            style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.32)' }}
            data-testid="brand-back"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5 text-[#F5D76E]" />
          </button>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#F5D76E]">{tenantName}</p>
          <div className="w-10" />
        </div>

        <section className="text-center">
          <div className="relative inline-flex">
            <div
              className="w-24 h-24 rounded-full p-3 flex items-center justify-center"
              style={{
                background: 'radial-gradient(circle, rgba(245,215,110,0.22), rgba(255,255,255,0.06))',
                border: '1px solid rgba(255,255,255,0.16)',
                boxShadow: '0 24px 70px rgba(0,0,0,0.34)',
              }}
            >
              <img src={resolvedLogo} alt={tenantName} className="max-w-full max-h-full object-contain" />
            </div>
            <span className="absolute right-2 bottom-2 w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center ring-2 ring-[#05231e]">
              <BadgeCheck className="w-4 h-4 text-white" />
            </span>
          </div>
          <h1 className="mt-3 text-xl sm:text-2xl font-black tracking-tight uppercase">{tenantName}</h1>
          <span className="inline-flex items-center gap-1.5 mt-2 rounded-full px-2.5 py-0.5 text-[11px] font-black text-[#F5D76E] bg-[#D4AF37]/12 border border-[#D4AF37]/25">
            <BadgeCheck className="w-3.5 h-3.5" /> Official Brand
          </span>
        </section>

        <section
          className="grid grid-cols-3 gap-2.5 mt-5 rounded-2xl p-3"
          style={{ background: 'rgba(3,27,24,0.82)', border: '1px solid rgba(212,175,55,0.20)' }}
          data-testid="brand-stats"
        >
          <div className="text-center">
            <Users className="w-5 h-5 mx-auto text-purple-300 mb-1" />
            <p className="text-lg font-black">25k+</p>
            <p className="text-[10px] uppercase tracking-[0.12em] text-gray-300">Followers</p>
          </div>
          <div className="text-center">
            <Trophy className="w-5 h-5 mx-auto text-[#F5D76E] mb-1" />
            <p className="text-lg font-black">{gameCount}+</p>
            <p className="text-[10px] uppercase tracking-[0.12em] text-gray-300">Games</p>
          </div>
          <div className="text-center">
            <Gem className="w-5 h-5 mx-auto text-emerald-300 mb-1" />
            <p className="text-lg font-black">200k+</p>
            <p className="text-[10px] uppercase tracking-[0.12em] text-gray-300">Rewards</p>
          </div>
        </section>

        <section className="mt-5" data-testid="brand-socials">
          <h2 className="text-sm font-black mb-3 flex items-center gap-2 text-[#F5D76E]">
            <Globe className="w-4 h-4" /> Connect With Us
          </h2>
          <div className="space-y-3">
            {socialLinks.length > 0 ? socialLinks.map((link) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-3 rounded-2xl p-3 transition hover:scale-[1.01]"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid={`brand-social-${link.label.toLowerCase()}`}
                >
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${link.tone}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block text-sm font-black">{link.label}</span>
                    <span className="block text-xs text-gray-300 truncate">{link.value}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-bold text-[#F5D76E]">
                    {link.action} <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </a>
              );
            }) : (
              <div className="rounded-2xl p-4 text-center text-sm text-gray-300" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                Support channels are not configured yet. Open Support from the bottom nav.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
